import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * Manages variant selection and price updates for Shop the look drawer rows.
 *
 * @extends {Component}
 */
class ShopTheLookDrawerProduct extends Component {
  /** @type {Array<{ id: number, price: number, compare_at_price: number | null, available: boolean, option1?: string, option2?: string, option3?: string }>} */
  #variants = [];

  /** @type {number} */
  #colorOptionPosition = 0;

  /** @type {HTMLSelectElement | null} */
  #variantSelect = null;

  connectedCallback() {
    super.connectedCallback();

    const variantsScript = this.querySelector('script[data-shop-the-look-variants]');
    if (variantsScript?.textContent) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent);
      } catch {
        this.#variants = [];
      }
    }

    this.#colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);
    this.#variantSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector('.shop-the-look-drawer__variant-select-input')
    );

    this.#variantSelect?.addEventListener('change', this.#onVariantChange);

    if (this.#variantSelect) {
      this.#onVariantChange();
    }

    this.#checkMultipleColors();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#variantSelect?.removeEventListener('change', this.#onVariantChange);
  }

  #checkMultipleColors() {
    const groupTag = this.dataset.groupTag;
    if (!groupTag) return;

    const moreColoursLink = this.querySelector('[ref="moreColoursLink"]');
    if (!moreColoursLink) return;

    const r = typeof Theme !== 'undefined' ? Theme.routes : {};
    let allProductsUrl = r.all_products_collection_url || '/collections/all';
    if (allProductsUrl.endsWith('/')) {
      allProductsUrl = allProductsUrl.slice(0, -1);
    }
    const url = `${allProductsUrl}/${encodeURIComponent(groupTag)}?view=json`;

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        let products = Array.isArray(data) ? data : (data?.products || []);
        if (products.length > 1) {
          moreColoursLink.style.display = '';
        }
      })
      .catch((err) => {
        console.warn('Error checking multiple colors:', err);
      });
  }

  #onVariantChange = () => {
    const select = this.#variantSelect;
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    const variantId = select?.value || variantIdInput?.value;

    if (!variantId) {
      this.#updateAddButton(null);
      return;
    }

    const variant = this.#variants.find((entry) => String(entry.id) === String(variantId));
    if (!variant || !variantIdInput) return;

    variantIdInput.value = String(variant.id);
    variantIdInput.disabled = !variant.available;

    this.#updatePrice(variant);
    this.#updateColorLabel(variant);
    this.#updateAddButton(variant);
  };

  /**
   * @param {{ available?: boolean } | null} variant
   */
  #updateAddButton(variant) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
    if (!button) return;

    // button.disabled = !variant?.available;
    button.disabled = variant === null ? false : !variant.available;
  }

  /**
   * @param {{ option1?: string, option2?: string, option3?: string }} variant
   */
  // #updateColorLabel(variant) {
  //   const colorLabel = /** @type {HTMLElement | null} */ (this.querySelector('[ref="colorLabel"]'));
  //   if (!colorLabel || !this.#colorOptionPosition) return;

  //   const colorValue = variant[`option${this.#colorOptionPosition}`];
  //   if (typeof colorValue === 'string' && colorValue.length > 0) {
  //     colorLabel.textContent = colorValue;
  //     colorLabel.hidden = false;
  //   }
  // }

  #updateColorLabel(variant) {
    const colorLabel = /** @type {HTMLElement | null} */ (this.querySelector('[ref="colorLabel"]'));
    if (!colorLabel || !this.#colorOptionPosition) return;

    const colorValue = variant[`option${this.#colorOptionPosition}`];
    if (typeof colorValue === 'string' && colorValue.length > 0) {
      const parts = colorValue.split('/').map(p => p.trim());
      const first = parts[0];
      const isDuplicate = parts.every(p => p === first);

      colorLabel.textContent = isDuplicate ? first : parts.join(' / ');
      colorLabel.hidden = false;
    }
  }

  /**
   * @param {{ price: number, compare_at_price: number | null }} variant
   */
  #updatePrice(variant) {
    const priceContainer = /** @type {HTMLElement | null} */ (this.querySelector('[ref="priceContainer"]'));
    const discountBadge = /** @type {HTMLElement | null} */ (this.querySelector('[ref="discountBadge"]'));
    if (!priceContainer) return;

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || '';
    const price = formatMoney(variant.price, moneyFormat, currency);
    const compareAtPrice = variant.compare_at_price;

    if (compareAtPrice && compareAtPrice > variant.price) {
      const compareFormatted = formatMoney(compareAtPrice, moneyFormat, currency);
      const discountAmount = compareAtPrice - variant.price;
      const discountPercentage = Math.round((discountAmount * 100) / compareAtPrice);

      priceContainer.innerHTML = `
        <span class="price price--on-sale">
          <span class="visually-hidden">${priceContainer.dataset.salePriceLabel || ''}</span>
          <span class="price-item price-item--sale">${price}</span>
          <span class="visually-hidden">${priceContainer.dataset.regularPriceLabel || ''}</span>
          <span class="price-item price-item--regular"><s>${compareFormatted}</s></span>
        </span>
      `;

      if (discountBadge) {
        discountBadge.textContent = `-${discountPercentage}%`;
        discountBadge.hidden = false;
      }
    } else {
      priceContainer.innerHTML = `<span class="price"><span class="price-item">${price}</span></span>`;

      if (discountBadge) {
        discountBadge.hidden = true;
      }
    }
  }
}

if (!customElements.get('shop-the-look-drawer-product')) {
  customElements.define('shop-the-look-drawer-product', ShopTheLookDrawerProduct);
}
